import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import Button from "components/Button";
import Loading from "components/Loading";
import Modal from "components/Modal";
import Title from "components/Title";
import CardLayout from "components/form/CardLayout";
import FormLayout from "components/form/FormLayout";
import { FC, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import FieldWithTranslation from "components/inputs/FieldWithTranslation";
import TextInput from "components/inputs/TextInput";
import apiEndpoints from "configs/apiEndPoints";
import toastFunc from "utils/toast";
import { useMutationCustom, useQueryCustom } from "utils/useQueryCUstom";
import {
  transformFieldWithTranslationForServer,
  transformFieldWithTranslationFromServer,
} from "utils/transformerFunctions";
import { CONFIGS } from "../configs";
import { fieldNames, validation } from "./validation";

interface FormValues {
  title: any;
  listOrderNumber: number;
}

const Add: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const URL_ID = id || "";
  const [editInitialLoading, setEditInitialLoading] = useState(!!id);
  const { t } = useTranslation();

  const updateOrCreate = URL_ID
    ? (params: any) =>
        axios.put(apiEndpoints[CONFIGS.apiName].base + "/" + URL_ID, params)
    : (params: any) => axios.post(apiEndpoints[CONFIGS.apiName].base, params);

  const methods = useForm<FormValues>({
    resolver: yupResolver(validation(URL_ID, t)),
    shouldFocusError: true,
  });

  const { data: langData, isLoading: langLoading } = useQueryCustom({
    name: `${CONFIGS.title}_langs`,
    url: () => axios.get(apiEndpoints.languages.base),
    enabled: true,
  });

  const onSuccessDataById = ({ data }: any) => {
    const params = { ...data?.data };
    const transformedTitle = transformFieldWithTranslationFromServer(
      params?.title,
      langData.data.data.result,
      fieldNames.title
    );
    methods.reset({ ...params, ...transformedTitle });
    setEditInitialLoading(false);
  };

  useQueryCustom({
    name: `${CONFIGS.title}_getById`,
    url: () => axios.get(apiEndpoints[CONFIGS.apiName].base + "/" + URL_ID),
    onSuccess: onSuccessDataById,
    enabled: !!URL_ID && !langLoading,
  });

  const { isLoading, mutate: submitData } = useMutationCustom({
    url: updateOrCreate,
    invalidQuery: `${CONFIGS.title}_get`,
    onSuccess: () => {
      toastFunc.success({ title: "Success" });
      navigate(CONFIGS.routes.base);
    },
  });

  const onSubmit = (data: FormValues) => {
    const transformedTitle = transformFieldWithTranslationForServer(
      data.title,
      fieldNames.title
    );
    submitData({
      ...transformedTitle,
      listOrderNumber: data.listOrderNumber,
    });
  };

  return (
    <div>
      {editInitialLoading && (
        <Modal isOpen title={t("loadingData")}>
          <div className="flex justify-center items-center">
            <Loading size="w-8 h-8" />
          </div>
        </Modal>
      )}

      <Title
        hasBtn
        btnTitle={t("backTitle", { changeablePart: t(CONFIGS.title) })}
        title={
          URL_ID
            ? t("editTitle", { changeablePart: t(CONFIGS.title) })
            : t("addNew", { changeablePart: t(CONFIGS.title) })
        }
        url={CONFIGS.routes.base + CONFIGS.routes.list}
      />

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-4">
          <FormLayout
            main={
              <CardLayout head={{ title: t("generalInfo") }}>
                <FieldWithTranslation name={fieldNames.title} />
                <TextInput
                  type="number"
                  name={fieldNames.listOrderNumber}
                  label={fieldNames.listOrderNumber}
                />
              </CardLayout>
            }
            side={
              <CardLayout head={{ title: "action" }} className="sticky top-4">
                <Button
                  type="submit"
                  variant="success"
                  isFullSize
                  loading={isLoading}
                >
                  {t("submit")}
                </Button>
              </CardLayout>
            }
          />
        </form>
      </FormProvider>
    </div>
  );
};

export default Add;
